// src/components/NotesPanel.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import * as NoteService from '../services/NoteService.mjs';
import { UserContext } from '../context/UserContext';
import { useConfirm } from '../context/ConfirmContext.jsx';
import InlineBanner from './common/InlineBanner.jsx';
import '../css/NotesPanel.css';

/**
 * Props:
 *   file          – the current File object (needs file.id)
 *   onCountChange – fired with the note count whenever it changes, so a host can
 *                   label the section (the details modal's tab strip) without
 *                   fetching the notes a second time
 *
 * Self-contained notes/comments panel. Loads notes on mount, supports adding,
 * toggling "done", and deleting. A failed load shows a small error rather than
 * crashing the surrounding modal.
 */
const NotesPanel = ({ file, onCountChange }) => {
    const fileId = file?.id;
    const confirm = useConfirm();
    const userCtx = useContext(UserContext);
    const currentName =
        userCtx?.user?.displayName ||
        userCtx?.user?.name ||
        userCtx?.user?.email ||
        null;

    const [notes, setNotes]         = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [draft, setDraft]               = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addError, setAddError]         = useState(null);

    const [pendingIds, setPendingIds] = useState(() => new Set());

    // ── Load notes ──────────────────────────────────────────────────────────────
    const loadNotes = useCallback(async () => {
        if (!fileId) return;
        setIsLoading(true);
        setLoadError(null);
        try {
            const data = await NoteService.listNotes(fileId);
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
            setNotes(list);
        } catch (err) {
            console.error('Failed to load notes:', err);
            setLoadError('Could not load notes.');
        } finally {
            setIsLoading(false);
        }
    }, [fileId]);

    useEffect(() => { loadNotes(); }, [loadNotes]);

    // Report the count from one place rather than from every mutation path —
    // add, delete and reload all end up here.
    useEffect(() => { onCountChange?.(notes.length); }, [notes.length, onCountChange]);

    // ── Add a note ──────────────────────────────────────────────────────────────
    const handleAdd = async () => {
        const text = draft.trim();
        if (!text || isSubmitting || !fileId) return;

        setIsSubmitting(true);
        setAddError(null);
        try {
            const res = await NoteService.createNote(fileId, text);
            const created = res?.note;
            if (created) {
                setNotes((prev) => [...prev, created]);
            } else {
                // Fall back to a full refresh if the server didn't echo the note.
                await loadNotes();
            }
            setDraft('');
        } catch (err) {
            console.error('Failed to add note:', err);
            setAddError('Could not add note. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e) => {
        // Cmd/Ctrl+Enter submits.
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    // ── Toggle done ─────────────────────────────────────────────────────────────
    const handleToggleDone = async (note) => {
        if (!fileId) return;
        const nextDone = !note.done;
        markPending(note.id, true);
        // Optimistic update.
        setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, done: nextDone } : n)));
        try {
            const res = await NoteService.updateNote(fileId, note.id, { done: nextDone });
            if (res?.note) {
                setNotes((prev) => prev.map((n) => (n.id === note.id ? res.note : n)));
            }
        } catch (err) {
            console.error('Failed to update note:', err);
            // Revert on failure.
            setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, done: note.done } : n)));
        } finally {
            markPending(note.id, false);
        }
    };

    // ── Delete a note ───────────────────────────────────────────────────────────
    const handleDelete = async (note) => {
        if (!fileId) return;
        const ok = await confirm({
            title: 'Delete note?',
            message: 'Delete this note?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true,
        });
        if (!ok) return;
        markPending(note.id, true);
        try {
            await NoteService.deleteNote(fileId, note.id);
            setNotes((prev) => prev.filter((n) => n.id !== note.id));
        } catch (err) {
            // Surfaced globally by the axios interceptor (notification center).
            console.error('Failed to delete note:', err);
        } finally {
            markPending(note.id, false);
        }
    };

    // ── Pending-id helpers ──────────────────────────────────────────────────────
    const markPending = (id, on) => {
        setPendingIds((prev) => {
            const next = new Set(prev);
            if (on) next.add(id); else next.delete(id);
            return next;
        });
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className="notes-panel">
            <div className="notes-list">
                {isLoading && notes.length === 0 && (
                    <p className="notes-state notes-state--muted">Loading notes…</p>
                )}

                {loadError && (
                    <InlineBanner variant="danger" onDismiss={() => setLoadError(null)}>
                        {loadError}
                    </InlineBanner>
                )}

                {!isLoading && !loadError && notes.length === 0 && (
                    <p className="notes-state notes-state--muted">No notes yet. Add the first one below.</p>
                )}

                {notes.map((note) => {
                    const busy = pendingIds.has(note.id);
                    return (
                        <div
                            key={note.id}
                            className={`note-row${note.done ? ' note-row--done' : ''}${busy ? ' note-row--busy' : ''}`}
                        >
                            <label className="note-check" title={note.done ? 'Mark as not done' : 'Mark as done'}>
                                <input
                                    type="checkbox"
                                    checked={!!note.done}
                                    onChange={() => handleToggleDone(note)}
                                    disabled={busy}
                                />
                            </label>

                            <div className="note-body">
                                <div className="note-text">{note.text}</div>
                                <div className="note-meta">
                                    <span className="note-author">{note.authorName || 'Someone'}</span>
                                    <span className="note-sep">·</span>
                                    <span className="note-date" title={formatFull(note.createdAt)}>
                                        {formatRelative(note.createdAt)}
                                    </span>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="note-delete"
                                onClick={() => handleDelete(note)}
                                disabled={busy}
                                title="Delete note"
                                aria-label="Delete note"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <path
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M3 6h18M8 6V4h8v2m-9 0v14a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6M10 11v6M14 11v6"
                                    />
                                </svg>
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="notes-add">
                {addError && (
                    <InlineBanner variant="danger" onDismiss={() => setAddError(null)}>
                        {addError}
                    </InlineBanner>
                )}
                <textarea
                    className="notes-add-input"
                    placeholder={currentName ? `Add a note as ${currentName}…` : 'Add a note…'}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={2}
                    disabled={isSubmitting}
                />
                <div className="notes-add-footer">
                    <span className="notes-add-hint">Ctrl/⌘ + Enter to post</span>
                    <button
                        type="button"
                        className="notes-add-btn"
                        onClick={handleAdd}
                        disabled={isSubmitting || draft.trim().length === 0}
                    >
                        {isSubmitting ? 'Adding…' : 'Add note'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Date helpers ─────────────────────────────────────────────────────────────
// Timestamps may arrive as ISO strings, epoch ms, or Firestore { _seconds }.
function toMillis(raw) {
    if (!raw) return 0;
    if (typeof raw === 'number') return raw;
    if (raw._seconds) return raw._seconds * 1000;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? 0 : t;
}

function formatFull(raw) {
    const ms = toMillis(raw);
    if (!ms) return '';
    return new Date(ms).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

function formatRelative(raw) {
    const ms = toMillis(raw);
    if (!ms) return '—';
    const diff = Date.now() - ms;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default NotesPanel;
